/**
 * F26 (Wave 2D) — admin notifications API client (real surface).
 *
 * Admin-only test surfaces (gated by `notifications:dry_run`, super_admin
 * only):
 *   POST /admin/notifications/dry-run
 *   POST /admin/notifications/test-send
 *
 * FIX 5.27 (§12.9-7) — the admin-inbox methods (`/me/notifications/*`) were
 * removed: that route family is consumer auth (`in_app_notifications.
 * recipient_user_id` references `users`), so every call 401'd for an admin
 * token and nothing called them. Admins have no in-app inbox; their alerts are
 * email-only (`AdminEmailService`). A post-MVP admin inbox needs an
 * admin-recipient API path first.
 *
 * Errors propagate as ApiError; the consumer pages translate them via the
 * shared notifications error catalogue in `@bravemove/i18n`.
 */
import type { DryRunInput, DryRunResult, TestSendInput } from '@bravemove/types';
import { adminFetch } from '../auth/apiClient';

export interface TestSendResponse {
  readonly sourceEventId: string;
  readonly inboxNotificationId: string | null;
}

export const adminNotificationsApi = {
  /**
   * POST /admin/notifications/dry-run — super_admin only.
   *
   * Returns what WOULD be dispatched (per-channel decisions, recipient
   * details, active token count) without actually writing an inbox row or
   * calling FCM/SES/WhatsApp. The dispatcher's NotificationEvent union lives
   * in apps/api; the client passes `kind + payload` as a stringly-typed
   * envelope per Decision F1.
   */
  dryRun: async (input: DryRunInput): Promise<DryRunResult> => {
    // The API returns the un-wrapped DryRunResult (no `data` envelope).
    return adminFetch<DryRunResult>(`/admin/notifications/dry-run`, {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
  },

  /**
   * POST /admin/notifications/test-send — super_admin only.
   *
   * Sends a real test event to the admin's own user_id. Requires the admin
   * to also have a `users` row; otherwise 422 TEST_SEND_NO_USER_ACCOUNT.
   */
  testSend: async (input: TestSendInput): Promise<TestSendResponse> => {
    // The API returns the un-wrapped dispatch result (no `data` envelope).
    return adminFetch<TestSendResponse>(`/admin/notifications/test-send`, {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
  },
};
